import copy from '../../copy/en/common.json' with { type: 'json' };
/**
 * The one fetch wrapper every data call goes through.
 *
 * - Attaches `Authorization: Bearer <token>` from the session.
 * - 401 → `onUnauthorized()` (the app clears the session and shows login).
 * - 403 `{ error: "mfa_required" }` → `onMfaRequired()` (the app routes to /mfa).
 * - Every other non-2xx → `ApiError(status, code)` where `code` is the server's
 *   `error` string (e.g. `last_admin`, `list_unavailable`) so screens can map it
 *   to a human message. `reason` carries the optional server `reason` field
 *   (e.g. `postgres_required`).
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    public readonly reason?: string,
  ) {
    super(code === "view_as_read_only" ? copy.api.thisPreviewIsReadOnlyReturnTo : code);
    this.name = "ApiError";
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

/** 503 `list_unavailable` / `postgres_required`: the server has no database. */
export function isDatabaseUnavailable(e: unknown): boolean {
  return isApiError(e) && (e.status === 503 || e.reason === "postgres_required");
}

export interface ApiClientOptions {
  getToken: () => Promise<string | null> | string | null;
  onUnauthorized?: () => void;
  onMfaRequired?: () => void;
  fetchImpl?: typeof fetch;
  /** Defaults to `/api`. */
  baseUrl?: string;
  /** Immutable request scope; captured clients must never inherit a later identity. */
  viewAsUserId?: string;
  isCurrent?: () => boolean;
  scopeSignal?: AbortSignal;
}

export interface RequestInitLite {
  method?: "GET" | "HEAD" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
}

export interface ApiClient {
  request<T>(path: string, init?: RequestInitLite): Promise<T>;
  get<T>(path: string, signal?: AbortSignal): Promise<T>;
  post<T>(path: string, body?: unknown): Promise<T>;
  patch<T>(path: string, body?: unknown): Promise<T>;
  /** Authenticated binary download (PDFs). Same 401/403 handling. */
  fetchBlob(path: string): Promise<Blob>;
}

async function readErrorBody(res: Response): Promise<{ error?: string; reason?: string }> {
  try {
    const text = await res.text();
    if (!text) return {};
    return JSON.parse(text) as { error?: string; reason?: string };
  } catch {
    return {};
  }
}

export function createApiClient(opts: ApiClientOptions): ApiClient {
  const fetchImpl = opts.fetchImpl ?? ((input, init) => fetch(input, init));
  const base = (opts.baseUrl ?? "/api").replace(/\/+$/, "");
  function assertCurrent() {
    if (opts.scopeSignal?.aborted || opts.isCurrent?.() === false) throw new DOMException("Portal context changed", "AbortError");
  }

  async function raw(path: string, init: RequestInitLite & { accept?: string } = {}): Promise<Response> {
    assertCurrent();
    if (opts.viewAsUserId && !["GET", "HEAD"].includes(init.method ?? "GET")) throw new ApiError(403, "view_as_read_only");
    const token = await opts.getToken();
    assertCurrent();
    const headers: Record<string, string> = {
      Accept: init.accept ?? "application/json",
    };
    if (token) headers.Authorization = `Bearer ${token}`;
    if (opts.viewAsUserId) headers["X-OHF-View-As"] = opts.viewAsUserId;
    if (init.body !== undefined) headers["Content-Type"] = "application/json";
    const res = await fetchImpl(`${base}${path.startsWith("/") ? path : `/${path}`}`, {
      method: init.method ?? "GET",
      headers,
      body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
      signal: opts.scopeSignal ? (init.signal ? AbortSignal.any([init.signal, opts.scopeSignal]) : opts.scopeSignal) : init.signal,
      cache: "no-store",
    });
    assertCurrent();
    if (res.status === 401) {
      opts.onUnauthorized?.();
      throw new ApiError(401, "unauthorized");
    }
    if (!res.ok) {
      const body = await readErrorBody(res);
      assertCurrent();
      if (res.status === 403 && body.error === "mfa_required") {
        opts.onMfaRequired?.();
        throw new ApiError(403, "mfa_required");
      }
      throw new ApiError(res.status, body.error ?? `http_${res.status}`, body.reason);
    }
    return res;
  }

  async function request<T>(path: string, init?: RequestInitLite): Promise<T> {
    const res = await raw(path, init);
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    assertCurrent();
    return (text ? JSON.parse(text) : undefined) as T;
  }

  return {
    request,
    get: (path, signal) => request(path, { signal }),
    post: (path, body) => request(path, { method: "POST", body: body ?? {} }),
    patch: (path, body) => request(path, { method: "PATCH", body: body ?? {} }),
    async fetchBlob(path) {
      const res = await raw(path, { accept: "*/*" });
      const blob = await res.blob();
      assertCurrent();
      return blob;
    },
  };
}
